import type { DeliveryMethod, DeliveryMethodType, Product } from '@libs/common-types';

import type { FormFields } from '@/entities/purchase-form';

const DELIVERY_METHODS_FOR_AUTOSHIP: DeliveryMethodType[] = ['STH', 'BOPIS', 'SDD'];

type VariantSupportsAutoshipPurchaseParams = {
    deliveryMethods: Product['deliveryMethods'];
    isAutoshipEnabledOnVariant: boolean;
};

type IsDeliveryMethodAvailableParams = {
    deliveryMethodName: DeliveryMethodType;
    purchaseOption: FormFields['purchaseOption'];
    deliveryMethodData?: DeliveryMethod;
};

/**
 * Determines if a delivery method is available for the purchase option using
 * `availablePurchaseOptions` from Crawl.
 */
export function isDeliveryMethodAvailable({
    purchaseOption,
    deliveryMethodData,
}: IsDeliveryMethodAvailableParams): boolean {
    const purchaseOptionKey = purchaseOption === 'autoship' ? 'autoship' : 'oneTime';
    return deliveryMethodData?.availablePurchaseOptions?.[purchaseOptionKey] ?? false;
}

/**
 * Whether the variant supports autoship through any fulfillment method (aligned with PDP delivery tiles).
 */
export function variantSupportsAutoshipPurchase({
    deliveryMethods,
    isAutoshipEnabledOnVariant,
}: VariantSupportsAutoshipPurchaseParams): boolean {
    if (!isAutoshipEnabledOnVariant) {
        return false;
    }

    return DELIVERY_METHODS_FOR_AUTOSHIP.some(deliveryMethodName =>
        isDeliveryMethodAvailable({
            purchaseOption: 'autoship',
            deliveryMethodName,
            deliveryMethodData: deliveryMethods[deliveryMethodName],
        })
    );
}
